/**
 * A non-profit's giving report, a tab on Reports for someone who may see donor
 * giving (migration 0084; N6 of the #55 plan, §B.17.5). The figures are the
 * API's (apps/api/src/services/donors.ts); this lays them out.
 *
 * Every party who gave is listed, and one not marked a donor says so (review
 * DN-3). The two flags are Publication 1771's: a single gift whose deductible
 * part is $250 or more needs a written acknowledgment, and an event payment
 * over $75 with goods or services given in return needs a disclosure. Each row
 * links to that donor's year-end letter.
 *
 * A private foundation's donor whose contributions reach Schedule B's $5,000
 * is marked "$5,000 Or More This Year" (0085, round 118's PF-3). It is named
 * for what it is and never "Substantial Contributor", which is IRC
 * 507(d)(2)'s cumulative test and the CPA's.
 */

import { Link } from 'react-router-dom';
import type { GivingReport as Giving } from '../api.js';
import { formatCents } from '../lib/money.js';
import { InfoNote } from './InfoNote.js';

export function GivingReport({ report }: { report: Giving }) {
  const { year } = report;
  return (
    <div className="report">
      <h2>Giving</h2>
      <p className="muted">
        Gifts received in {year}, by the day they arrived.{' '}
        <InfoNote label="What Counts As A Gift">
          A gift is money or property received: a contribution, or a pledge paid from the bank. A pledge made is
          the donor&apos;s promise, shown here with what remains open and left off the letter, since a donor deducts
          what they paid. A pledge reduced is a change to the pledge, never a gift. In-kind gifts are shown at the
          books&apos; value here and without a value on the letter. A single gift of $250 or more after any goods or
          services given needs a written acknowledgment, and an event payment over $75 with something given in return
          needs a disclosure; the year-end letter does both.
        </InfoNote>
        {report.scheduleBThresholdCents != null && (
          <>
            {' '}
            <InfoNote label="What $5,000 Or More This Year Means">
              A donor whose contributions this year, less anything they received in return, come to{' '}
              {formatCents(report.scheduleBThresholdCents)} or more is listed on the 990-PF&apos;s Schedule B; your CPA
              confirms the threshold. It is not the substantial-contributor test, which is cumulative: more than $5,000
              and more than 2% of everything the foundation has received since it began. That test is your CPA&apos;s,
              and a donor they find to be one is marked a disqualified person, as a substantial contributor, on
              Parties.
            </InfoNote>
          </>
        )}
      </p>
      {report.donors.length === 0 ? (
        <p className="muted">No gifts or pledges recorded in {year}.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines report-classes">
            <thead>
              <tr>
                <th>Donor</th>
                <th className="num">Cash</th>
                <th className="num">In-Kind</th>
                <th className="num">Events</th>
                <th className="num">Goods or Services Provided</th>
                <th className="num">Pledged</th>
                <th className="num">Pledges Changed</th>
                <th className="num">Open Pledges</th>
                <th>Needs</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {report.donors.map((donor) => (
                <tr key={donor.partyId ?? 'no-donor'}>
                  <td>
                    {donor.partyId === null ? (
                      // DO-2: contributions on entries naming nobody, so the omission is seen.
                      <>
                        No donor named <span className="muted"> · Name the party on these entries to thank them</span>
                      </>
                    ) : (
                      <>
                        {donor.name}
                        {!donor.isDonor && <span className="muted"> · Not marked as a donor on Parties</span>}
                        {donor.scheduleB === true && <strong className="giving-mark"> · $5,000 Or More This Year</strong>}
                      </>
                    )}
                  </td>
                  <td className="num">{formatCents(donor.cash.totalCents)}</td>
                  <td className="num">{formatCents(donor.inKind.totalCents)}</td>
                  <td className="num">{formatCents(donor.event.totalCents)}</td>
                  <td className="num">{formatCents(donor.goodsServicesCents)}</td>
                  <td className="num">{formatCents(donor.pledgesMadeCents)}</td>
                  <td className="num">{formatCents(donor.pledgeChangesCents)}</td>
                  <td className="num">{formatCents(donor.pledgesOpenCents)}</td>
                  <td>
                    {[donor.needsAcknowledgment && 'Acknowledgment', donor.needsDisclosure && 'Disclosure']
                      .filter(Boolean)
                      .join(', ')}
                  </td>
                  <td>
                    {donor.partyId !== null && <Link to={`/donors/${donor.partyId}/letter?year=${year}`}>Year-End Letter</Link>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
